import { PageType, PreMadeTemplateType } from "@reconvert/reconvert-utils";
import { ConditionType } from "../hooks/useAndOrGrouping";

export const Platform = {
  Shopify: "shopify",
  Wix: "wix",
} as const;

export type PlatformType = (typeof Platform)[keyof typeof Platform];

export type AllowedPlatform = Array<PlatformType>;

export interface ConditionsResponse {
  success: boolean;
  data: Condition[];
}

export interface SlotConfig {
  conditionId: string;
  emptySlotOption: EmptySlotOption;
  id: string;
  location: string;
  slotId: string;
  slotNo: Number;
}

export interface Condition {
  priority: number;
  rule: any;
  name: string;
  status: boolean;
  isDefault: boolean;
  scid: number;
  storeName: string;
  id: string;
  platformStoreId?: string;
  location?: string;
  variants?: Variant[];
  slotConfig?: SlotConfig[];
}

export interface ConditionRule {
  id: string;
  conditionType: ConditionType;
  parentId: string | null;
  fact: string;
  operator: string;
  value: string[];
  extraFields: {
    condition: string;
    operatorValue: string;
    [key: string]: any;
  };
}

export interface DefaultCondition {
  name: string;
  isDefault: number;
  location: PageType;
}

export interface AddCondition {
  priority?: number;
  name: string;
  platformStoreId: string;
  location: string;
  rule: any;
  isDefault?: number;
}

export interface ConditionInfo extends Condition {
  slots?: Slot[];
  variants?: Variant[];
  slotConfig?: SlotConfig[];
}

export type EmptySlotOption = "CONTINUE" | "STOP";

export enum SlotStatus {
  ACTIVE = "ACTIVE",
  SUSPENDED = "SUSPENDED",
  EDITOR_VIEWED = "EDITOR_VIEWED",
  NEVER_DETECTED = "NEVER_DETECTED",
  UNDEFINED_SLOT_ACTIVE = "UNDEFINED_SLOT_ACTIVE",
  UNDEFINED_SLOT_EDITOR_VIEWED = "UNDEFINED_SLOT_EDITOR_VIEWED",
  SLOT_DELETED = "SLOT_DELETED",
  SLOT_DETECTED = "SLOT_DETECTED",
  PAGE_VIEWED = "PAGE_VIEWED",
}

export interface Slot {
  slotNo: number;
  places: string[];
  variants: Variant[];
  name: string;
  _id: string;
  id: string;
  status: boolean;
  emptySlotOption: EmptySlotOption;
  isABTestingEnabled: boolean;
  slotStatus: SlotStatus;
  type: PreMadeTemplateType;
  placement?: string;
  placementStatus?: boolean;
}

export interface Variant {
  name: string;
  coverage: number;
  slotId: string;
  widgets: Widget[];
  id: string;
  status: boolean;
  abImpressionShare: number;
  conditionId: string;
  slotNo: number;
}

export interface Widget {
  upsellInfo: any;
  widgets: Widget[];
  category: string;
  status: boolean;
  name: string;
  text: string;
  type: string;
  isVisible: boolean;
  slotId: string;
  variantId: string;
  blocks: any[];
  parentWidgetId: string;
  id: string;
  content: any;
  /** QUANTITY_BREAKS tiers. */
  offers?: QuantityBreaksOffer[];
}

export interface QuantityBreaksOffer {
  id: string;
  type: string;
  title: string;
  subTitle?: string;
  label?: string;
  quantity: number;
  discount?: {
    type: "FULL_PRICE" | "PERCENTAGE_OFF" | "AMOUNT_OFF" | "FIXED_PRICE";
    value: number;
  };
  isVisible: boolean;
  displayOrder: number;
}

export interface ProductData {
  id: string;
  images: string[];
  title: string;
  subTitle?: string;
  variants: ProductVariant[];
}

export interface ProductVariant {
  id: string;
  title: string;
  price: string;
}

export interface ABTestVariant {
  name: string;
  slotNo: number;
  coverage: number;
  status: boolean;
  abImpressionShare: number;
  id: string;
  slotId: string;
  slotName: string;
  conditionId: string;
  conditionName: string;
  location: string;
}

export interface WidgetAnalytics {
  content: any;
  name: string;
  text?: string;
  type: string;
  category: string;
  isVisible: boolean;
  slotId: string;
  variantId: string;
  parentWidgetId?: string;
  blocks: any[];
  widgets: WidgetAnalytics[];
  createdAt?: string;
  updatedAt?: string;
  id: string;
  upsellInfo?: {
    type: string;
    settings: any;
  };
  impressions: string;
  clicks: string;
  conversions: string;
  revenue: string;
  cvr: string;
  ctr: string;
  declined: string;
  declinedDiffPercentage: string;
  impressionsDiffPercentage: string;
  clicksDiffPercentage: string;
  conversionsDiffPercentage: string;
  revenueDiffPercentage: string;
  cvrDiffPercentage: string;
  ctrDiffPercentage: string;
}

export interface WidgetAnalytics {
  content: any;
  name: string;
  text?: string;
  type: string;
  category: string;
  isVisible: boolean;
  slotId: string;
  variantId: string;
  parentWidgetId?: string;
  blocks: any[];
  widgets: WidgetAnalytics[];
  createdAt?: string;
  updatedAt?: string;
  id: string;
  upsellInfo?: {
    type: string;
    settings: any;
  };
  impressions: string;
  clicks: string;
  conversions: string;
  revenue: string;
  cvr: string;
  ctr: string;
  declined: string;
  declinedDiffPercentage: string;
  impressionsDiffPercentage: string;
  clicksDiffPercentage: string;
  conversionsDiffPercentage: string;
  revenueDiffPercentage: string;
  cvrDiffPercentage: string;
  ctrDiffPercentage: string;
}